import {
  useState,
  useEffect,
  useReducer,
  useContext,
  useCallback,
} from 'react';
import toastError from '../../toasts/toastError';
import { toast } from 'react-toastify';

import {
  Chip,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  InputAdornment,
  useTheme,
} from '@mui/material';

import { MoreHoriz, Search, DeleteOutline, Edit } from '@mui/icons-material';

import MainContainer from '../../components/MainContainer';
import MainHeader from '../../components/MainHeader';
import MainHeaderButtonsWrapper from '../../components/MainHeaderButtonsWrapper';
import Title from '../../components/Title';
import TableRowSkeleton from '../../components/TableRowSkeleton';
import TagModal from '../../components/TagModal';
import ConfirmationModal from '../../components/ConfirmationModal';
import ContactTagListModal from '../../components/ContactTagListModal';

import api from '../../services/api';
import { i18n } from '../../translate/i18n';
import { AuthContext } from '../../context/Auth/AuthContext';
import { getStyled } from './styles';

const reducer = (state, action) => {
  switch (action.type) {
    case 'LOAD_TAGS': {
      const newTags = action.payload;
      const existingIds = new Set(state.map((tag) => tag.id));
      const uniqueNewTags = newTags.filter((tag) => !existingIds.has(tag.id));
      return [...state, ...uniqueNewTags];
    }
    case 'UPDATE_TAGS': {
      const tag = action.payload;
      const tagIndex = state.findIndex((s) => s.id === tag.id);

      if (tagIndex !== -1) {
        const newState = [...state];
        newState[tagIndex] = tag;
        return newState;
      } else {
        return [tag, ...state];
      }
    }
    case 'DELETE_TAGS': {
      const tagId = action.payload;
      return state.filter((tag) => tag.id !== tagId);
    }
    case 'RESET':
      return [];
    default:
      return state;
  }
};

const Tags = () => {
  const theme = useTheme();
  const classes = getStyled(theme);

  const { user, socket } = useContext(AuthContext);

  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedTagName, setSelectedTagName] = useState('');
  const [selectedTag, setSelectedTag] = useState(null);
  const [deletingTag, setDeletingTag] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState('');
  const [tags, dispatch] = useReducer(reducer, []);
  const [tagModalOpen, setTagModalOpen] = useState(false);

  useEffect(() => {
    const fetchMoreTags = async () => {
      setLoading(true);

      try {
        const { data } = await api.get('/tags/', {
          params: { searchParam, pageNumber, kanban: 0 },
        });
        dispatch({ type: 'LOAD_TAGS', payload: data.tags });
        setHasMore(data.hasMore);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        toastError(err);
      }
    };

    if (pageNumber > 0) {
      fetchMoreTags();
    }
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const onCompanyTags = (data) => {
      if (data.action === 'update' || data.action === 'create') {
        dispatch({ type: 'UPDATE_TAGS', payload: data.tag });
      }

      if (data.action === 'delete') {
        dispatch({ type: 'DELETE_TAGS', payload: +data.tagId });
      }
    };
    socket.on(`company${user.companyId}-tag`, onCompanyTags);

    return () => {
      socket.off(`company${user.companyId}-tag`, onCompanyTags);
    };
  }, [socket, user.companyId]);

  const handleOpenTagModal = useCallback(() => {
    setSelectedTag(null);
    setTagModalOpen(true);
  }, []);

  const handleCloseTagModal = useCallback(() => {
    setSelectedTag(null);
    setTagModalOpen(false);
  }, []);

  const handleSearch = (event) => {
    const newSearchParam = event.target.value.toLowerCase();
    setSearchParam(newSearchParam);
    setPageNumber(1);
    dispatch({ type: 'RESET' });
  };

  const handleEditTag = useCallback((tag) => {
    setSelectedTag(tag);
    setTagModalOpen(true);
  }, []);

  const handleShowContacts = useCallback((tag) => {
    setContactModalOpen(true);
    setSelectedTagName(tag);
  }, []);

  const handleCloseContactModal = useCallback(() => {
    setContactModalOpen(false);
    setSelectedTagName('');
  }, []);

  const handleDeleteTag = async (tagId) => {
    try {
      await api.delete(`/tags/${tagId}`);
      toastSuccess(i18n.t('tags.toasts.deleted'));
    } catch (err) {
      toastError(err);
    }
    setDeletingTag(null);
    setSearchParam('');
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  // Remove duplicatas antes de renderizar
  const uniqueTags = tags.filter(
    (tag, index, self) => index === self.findIndex((t) => t.id === tag.id),
  );

  return (
    <MainContainer sx={classes.mainContainer}>
      {contactModalOpen && (
        <ContactTagListModal
          open={contactModalOpen}
          onClose={handleCloseContactModal}
          tag={selectedTagName}
        />
      )}
      <ConfirmationModal
        title={deletingTag && `${i18n.t('tags.confirmationModal.deleteTitle')}`}
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteTag(deletingTag.id)}
      >
        {i18n.t('tags.confirmationModal.deleteMessage')}
      </ConfirmationModal>
      <TagModal
        open={tagModalOpen}
        onClose={handleCloseTagModal}
        aria-labelledby='form-dialog-title'
        tagId={selectedTag && selectedTag.id}
        kanban={0}
      />
      <MainHeader>
        <Title>
          {i18n.t('tags.title')} ({uniqueTags.length})
        </Title>
        <MainHeaderButtonsWrapper>
          <TextField
            sx={classes.searchField}
            placeholder={i18n.t('contacts.searchPlaceholder')}
            type='search'
            variant='standard'
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant='outlined'
            onClick={handleOpenTagModal}
            sx={{ ...theme.custom.editButton, marginLeft: theme.spacing(1) }}
          >
            {i18n.t('tags.buttons.add')}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper sx={classes.mainPaper} variant='outlined' onScroll={handleScroll}>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell align='center'>{i18n.t('tags.table.id')}</TableCell>
              <TableCell align='center'>{i18n.t('tags.table.name')}</TableCell>
              <TableCell align='center'>
                {i18n.t('tags.table.contacts')}
              </TableCell>
              <TableCell align='center'>
                {i18n.t('tags.table.actions')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {uniqueTags.map((tag) => (
                <TableRow key={`tag-${tag.id}`}>
                  <TableCell align='center'>{tag.id}</TableCell>
                  <TableCell align='center'>
                    <Chip
                      variant='outlined'
                      sx={{
                        backgroundColor: tag.color,
                        textShadow: '1px 1px #000',
                        color: 'white',
                      }}
                      label={tag.name}
                      size='small'
                    />
                  </TableCell>
                  <TableCell align='center'>
                    {tag?.contacts?.length || 0}
                    <IconButton
                      size='small'
                      onClick={() => handleShowContacts(tag)}
                      disabled={!tag?.contacts || tag?.contacts?.length === 0}
                    >
                      <MoreHoriz />
                    </IconButton>
                  </TableCell>

                  <TableCell align='center'>
                    <IconButton size='small' onClick={() => handleEditTag(tag)}>
                      <Edit />
                    </IconButton>

                    <IconButton
                      size='small'
                      onClick={() => {
                        setConfirmModalOpen(true);
                        setDeletingTag(tag);
                      }}
                    >
                      <DeleteOutline
                        sx={{
                          color: theme.custom.colors.cancelButton,
                        }}
                      />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}

              {loading && <TableRowSkeleton key='skeleton' columns={4} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Tags;
